/**
 * API Configuration
 * Centralized API configuration for the application
 */

// Environment-based API URL
const getApiUrl = (): string => {
  // Check if custom API URL is provided
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Production URL
  return 'https://petromarinedb.codewealth.net';
};

export const API_CONFIG = {
  BASE_URL: getApiUrl(),
  API_VERSION: '/api',
  ADMIN_VERSION: '/admin',
  CONTENT_MANAGER_VERSION: '/content-manager/collection-types',
  TIMEOUT: 30000, // 30 seconds
  
  // Storage keys
  STORAGE_KEYS: {
    TOKEN: 'petromarine_jwt_token',
    USER: 'petromarine_user',
    REFRESH_TOKEN: 'petromarine_refresh_token',
    LANGUAGE: 'petromarine_language',
    THEME: 'petromarine_theme',
    PERMISSIONS: 'petromarine_permissions',
  },
  
  // Content Manager API endpoints (for admin users)
  CONTENT_MANAGER_ENDPOINTS: {
    CLIENTS: 'api::client.client',
    PROVIDERS: 'api::provider.provider',
    BRANCHES: 'api::branch.branch',
    EMPLOYEES: 'api::employee.employee',
    EMPLOYEE_DOCUMENTS: 'api::employee-document.employee-document',
    INVOICES: 'api::invoice.invoice',
    INVOICE_ITEMS: 'api::invoice-item.invoice-item',
    DEBIT_NOTES: 'api::debit-note.debit-note',
    DEDUCTIONS: 'api::deduction.deduction',
    PURCHASES: 'api::purchase.purchase',
    PURCHASE_ITEMS: 'api::purchase-item.purchase-item',
    BILLS: 'api::bill.bill',
    PAYMENT_ORDERS: 'api::payment-order.payment-order',
    PAYMENT_ORDER_ITEMS: 'api::payment-order-item.payment-order-item',
    TRANSACTIONS: 'api::transaction.transaction',
    PETTY_CASHES: 'api::petty-cash.petty-cash',
    PETTY_CASH_EXPENSES: 'api::petty-cash-expense.petty-cash-expense',
    BANKS: 'api::bank.bank',
    SAFES: 'api::safe.safe',
    CATEGORIES: 'api::category.category',
    CURRENCIES: 'api::currency.currency',
    CONTRACTS: 'api::contract.contract',
    FOAS: 'api::foa.foa',
    RANKS: 'api::rank.rank',
    CREW_CERTIFICATES: 'api::crew-certificate.crew-certificate',
    CREW_CHANGES: 'api::crew-change.crew-change',
    CREW_LISTS: 'api::crew-list.crew-list',
    CREW_EVALUATIONS: 'api::crew-evaluation.crew-evaluation',
    CANDIDATES: 'api::candidate.candidate',
    CANDIDATE_RATINGS: 'api::candidate-rating.candidate-rating',
    ASSESSMENT_POINTS: 'api::assessment-point.assessment-point',
    CAREERS: 'api::career.career',
    CARS: 'api::car.car',
    CAR_DOCUMENTS: 'api::car-document.car-document',
    CAR_PERMITS: 'api::car-permit.car-permit',
    PERSONNEL_PERMITS: 'api::personnel-permit.personnel-permit',
    CODE_PREFIXES: 'api::code-prefix.code-prefix',
    REQUISITIONS: 'api::requisition.requisition',
    REQUISITION_ITEMS: 'api::requisition-item.requisition-item',
    DELIVERY_NOTES: 'api::delivery-note.delivery-note',
    DELIVERY_NOTE_ITEMS: 'api::delivery-note-item.delivery-note-item',
    INVOICE_HIRES: 'api::invoice-hire.invoice-hire',
    INVOICE_HIRE_ITEMS: 'api::invoice-hire-item.invoice-hire-item',
    INVENTORY_CATEGORIES: 'api::inventory-category.inventory-category',
    PRODUCTS: 'api::product.product',
    ROLES: 'api::role.role',
    SYSTEM_USERS: 'admin::user.user',
    PERMISSIONS: 'api::permission.permission',
    LOCATIONS: 'api::location.location',
    OWNERS: 'api::owner.owner',
    DOCUMENT_TYPES: 'api::document-type.document-type',
    WEBSITE_HERO_SETTING: 'api::website-hero-setting.website-hero-setting',
    SERVICE_ITEMS: 'api::service-item.service-item',
    FLEET_VESSELS: 'api::fleet-vessel.fleet-vessel',
    CONTACT_OFFICES: 'api::contact-office.contact-office',
    PERMIT_LETTERS: 'api::permit-letter.permit-letter',
    LETTER_GENERATIONS: 'api::letter-generation.letter-generation',
    FINANCE_REPORTS: 'api::finance-report.finance-report',
    ERP_SETTINGS: 'api::erp-setting.erp-setting',
    EMPLOYEE_DEDUCTIONS: 'api::employee-deduction.employee-deduction',
    TIMESHEETS: 'api::timesheet.timesheet',
  },
  
  // API Endpoints
  ENDPOINTS: {
    // Admin Authentication (Strapi Admin endpoints)
    ADMIN_AUTH: {
      LOGIN: '/admin/login',
      ME: '/admin/users/me',
      RENEW_TOKEN: '/admin/access-token',
      FORGOT_PASSWORD: '/admin/forgot-password',
      RESET_PASSWORD: '/admin/reset-password',
    },
    
    // Core Business
    CLIENTS: '/clients',
    PROVIDERS: '/providers',
    BRANCHES: '/branches',
    EMPLOYEES: '/employees',
    EMPLOYEE_DOCUMENTS: '/employee-documents',
    
    // Financial
    INVOICES: '/invoices',
    INVOICE_ITEMS: '/invoice-items',
    DEBIT_NOTES: '/debit-notes',
    DEDUCTIONS: '/deductions',
    PURCHASES: '/purchases',
    PURCHASE_ITEMS: '/purchase-items',
    BILLS: '/bills',
    PAYMENT_ORDERS: '/payment-orders',
    PAYMENT_ORDER_ITEMS: '/payment-order-items',
    TRANSACTIONS: '/transactions',
    PETTY_CASHES: '/petty-cashes',
    PETTY_CASH_EXPENSES: '/petty-cash-expenses',
    
    // Banking
    BANKS: '/banks',
    SAFES: '/safes',
    CATEGORIES: '/categories',
    CURRENCIES: '/currencies',
    
    // Contracts & FOA
    CONTRACTS: '/contracts',
    FOAS: '/foas',
    
    // Crew & HR
    RANKS: '/ranks',
    CREW_CERTIFICATES: '/crew-certificates',
    CREW_CHANGES: '/crew-changes',
    CREW_LISTS: '/crew-lists',
    CREW_EVALUATIONS: '/crew-evaluations',
    
    // Recruitment
    CANDIDATES: '/candidates',
    CANDIDATE_RATINGS: '/candidate-ratings',
    ASSESSMENT_POINTS: '/assessment-points',
    CAREERS: '/careers',
    
    // Fleet Management
    CARS: '/cars',
    CAR_DOCUMENTS: '/car-documents',
    CAR_PERMITS: '/car-permits',
    PERSONNEL_PERMITS: '/personnel-permits',
    
    // Requisitions & Delivery
    CODE_PREFIXES: '/code-prefixes',
    REQUISITIONS: '/requisitions',
    REQUISITION_ITEMS: '/requisition-items',
    DELIVERY_NOTES: '/delivery-notes',
    DELIVERY_NOTE_ITEMS: '/delivery-note-items',
    
    // Invoice Hire
    INVOICE_HIRES: '/invoice-hires',
    INVOICE_HIRE_ITEMS: '/invoice-hire-items',
    
    // Inventory
    INVENTORY_CATEGORIES: '/inventory-categories',
    PRODUCTS: '/products',
    
    // System
    ROLES: '/roles',
    SYSTEM_USERS: '/system-users',
    PERMISSIONS: '/permissions',
    LOCATIONS: '/locations',
    OWNERS: '/owners',
    DOCUMENT_TYPES: '/document-types',
    
    // Website
    WEBSITE_HERO_SETTING: '/website-hero-setting',
    SERVICE_ITEMS: '/service-items',
    FLEET_VESSELS: '/fleet-vessels',
    CONTACT_OFFICES: '/contact-offices',
    
    // Letters & Reports
    PERMIT_LETTERS: '/permit-letters',
    LETTER_GENERATIONS: '/letter-generations',
    FINANCE_REPORTS: '/finance-reports',
    
    // ERP Settings
    ERP_SETTINGS: '/erp-settings',
  },
  
  // HTTP Status Codes
  STATUS_CODES: {
    OK: 200,
    CREATED: 201,
    NO_CONTENT: 204,
    BAD_REQUEST: 400,
    UNAUTHORIZED: 401,
    FORBIDDEN: 403,
    NOT_FOUND: 404,
    CONFLICT: 409,
    UNPROCESSABLE_ENTITY: 422,
    INTERNAL_SERVER_ERROR: 500,
    SERVICE_UNAVAILABLE: 503,
  },
  
  // Common query parameters
  QUERY_PARAMS: {
    POPULATE: 'populate',
    FILTERS: 'filters',
    SORT: 'sort',
    PAGINATION: 'pagination',
    FIELDS: 'fields',
    LOCALE: 'locale',
  },
};

// Helper function to build full API URL
export const getFullApiUrl = (endpoint: string): string => {
  return `${API_CONFIG.BASE_URL}${API_CONFIG.API_VERSION}${endpoint}`;
};

// Helper function to build full Admin URL (for admin endpoints)
export const getFullAdminUrl = (endpoint: string): string => {
  // Admin endpoints already include /admin in the path
  return `${API_CONFIG.BASE_URL}${endpoint}`;
};

// Helper function to build Content Manager URL (for admin users accessing collections)
export const getContentManagerUrl = (collectionType: string): string => {
  return `${API_CONFIG.BASE_URL}${API_CONFIG.CONTENT_MANAGER_VERSION}/${collectionType}`;
};

// Helper function to build query string
export const buildQueryString = (params: Record<string, any>): string => {
  const queryParams = new URLSearchParams();
  
  Object.keys(params).forEach(key => {
    const value = params[key];
    if (value !== undefined && value !== null && value !== '') {
      if (typeof value === 'object') {
        queryParams.append(key, JSON.stringify(value));
      } else {
        queryParams.append(key, value.toString());
      }
    }
  });
  
  const queryString = queryParams.toString();
  return queryString ? `?${queryString}` : '';
};

